import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { initAnalyticsAfterAuth } from "./analytics";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [venue, setVenue] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionWarning, setSubscriptionWarning] = useState(false);
  const [loading, setLoading] = useState(true);

  // applyAuth handles BOTH /auth/me payloads AND /auth/login + /auth/signup
  // responses since the backend now inlines subscription_status +
  // subscription_warning into login/signup TokenOut. This lets us avoid
  // the extra /auth/me round trip immediately after authenticating
  // (≈ 95 ms saved per login on warm preview).
  const applyAuth = (data) => {
    if (!data) return;
    if (data.user !== undefined) setUser(data.user || null);
    if (data.business !== undefined) setBusiness(data.business || null);
    if (data.venue !== undefined) setVenue(data.venue || null);
    if ("subscription_status" in data) setSubscriptionStatus(data.subscription_status || null);
    if ("subscription_warning" in data) setSubscriptionWarning(!!data.subscription_warning);
  };

  // Refresh = page reload / cold tab. Fetches /auth/me once.
  const refresh = useCallback(async () => {
    const t = localStorage.getItem("mm_token");
    if (!t) { setLoading(false); return; }
    try {
      const r = await api.get("/auth/me");
      applyAuth(r.data);
    } catch (e) {
      localStorage.removeItem("mm_token");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Side effect: gate PostHog/analytics behind successful auth.
  // Runs once per real user change. Deferred to browser idle inside
  // initAnalyticsAfterAuth so it never blocks first paint.
  useEffect(() => {
    if (user) initAnalyticsAfterAuth(user);
  }, [user]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("mm_token", r.data.token);
    applyAuth(r.data);
    // Fallback: only fetch /auth/me if backend hasn't inlined subscription
    // fields yet (old prod build). New build inlines them → no second call.
    if (!("subscription_status" in r.data)) {
      try { const me = await api.get("/auth/me"); applyAuth(me.data); } catch {}
    }
    return r.data;
  };
  const signup = async (payload) => {
    const r = await api.post("/auth/signup", payload);
    localStorage.setItem("mm_token", r.data.token);
    applyAuth(r.data);
    if (!("subscription_status" in r.data)) {
      try { const me = await api.get("/auth/me"); applyAuth(me.data); } catch {}
    }
    return r.data;
  };
  const logout = () => {
    localStorage.removeItem("mm_token");
    setUser(null); setBusiness(null); setVenue(null);
    setSubscriptionStatus(null); setSubscriptionWarning(false);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{
      user, business, venue,
      subscriptionStatus, subscriptionWarning,
      loading, login, signup, logout, refresh,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
